import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLiff } from '../../contexts/LiffContext.js';
import {
  getUserByLineIdToken,
  registerNewUserWithTenant,
} from '../../api/contextClient.js';
import { shouldUseMockLiff } from '../../hooks/useMockLiff.js';
import { logErrorToBackend } from '../../utils/errorLogger.js';
import { getGroupIdFromUrl } from '../../utils/getGroupIdFromUrl.js';
import { LiteActivityAssistantForm } from '@/components/LiteActivityAssistantForm';

/**
 * RegisterPage 組件
 * 正式版的 LIFF 登入綁定頁面
 * 處理 LINE 身份驗證、Google OAuth 登入、新用戶註冊和帳號綁定
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { liff, isReady, isLoggedIn, shouldUseLiff } = useLiff();
  const [isLoading, setIsLoading] = useState(true);
  const [step, setStep] = useState<
    'checking' | 'register' | 'registering' | 'success'
  >('checking');
  const [error, setError] = useState<string | null>(null);
  const [tenantForm, setTenantForm] = useState({
    name: '',
    type: '',
    customType: '',
  });
  const hasCheckedRef = useRef(false);

  // 檢查用戶身份（需在「檢查 URL 參數」的 useEffect 之前定義，以符合 exhaustive-deps）
  const checkUserIdentity = useCallback(async () => {
    if (hasCheckedRef.current) {
      return;
    }

    if (!isReady) {
      return;
    }

    // 在 LIFF 環境中，使用 LINE ID Token 檢查身份
    // 注意：即使 LIFF 初始化失敗（error 存在），也允許繼續嘗試（因為可能只是 LIFF ID 設定錯誤）
    if (!shouldUseLiff || (!liff && !shouldUseMockLiff())) {
      // 檢查是否在 LINE 環境中（透過 User-Agent 或 URL hash）
      const isInLine =
        navigator.userAgent.includes('Line') ||
        window.location.hash.includes('access_token');
      if (!isInLine) {
        setError('此頁面需要在 LINE 環境中使用');
        setIsLoading(false);
        return;
      }
      // 如果在 LINE 環境中但 LIFF 未初始化，可能是 LIFF ID 設定錯誤，但仍允許繼續嘗試
      console.warn(
        '⚠️ [RegisterPage] LIFF 未初始化，但檢測到 LINE 環境，繼續嘗試...'
      );
    }

    setIsLoading(true);
    setError(null);
    setStep('checking');

    try {
      // 取得 LINE ID Token
      // 注意：即使 isLoggedIn 為 false，也嘗試取得 token（因為有時 LIFF 狀態可能未更新）
      let lineIdToken: string | null = null;

      // 先嘗試從 URL hash 中取得 access_token（LINE 有時會在 URL 中提供）
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessTokenFromHash = hashParams.get('access_token');

      if (accessTokenFromHash) {
        console.log(
          '🔍 [RegisterPage] 從 URL hash 中發現 access_token，但需要 ID Token 才能註冊'
        );
        // 注意：access_token 不是 ID Token，我們仍然需要 ID Token
        // 但這表示 LINE 已經初始化了，只是 LIFF SDK 可能無法正常運作
      }

      if (liff) {
        try {
          // 先檢查是否已登入
          if (isLoggedIn || liff.isLoggedIn()) {
            lineIdToken = await liff.getIDToken();
          } else {
            // 如果未登入，嘗試取得 token（即使 isLoggedIn 為 false）
            console.log(
              '⚠️ [RegisterPage] 使用者未登入，但嘗試取得 ID Token...'
            );
            try {
              lineIdToken = await liff.getIDToken();
            } catch (getTokenError) {
              // 如果取得 token 失敗，可能是因為需要登入
              console.warn(
                '⚠️ [RegisterPage] 取得 ID Token 失敗，可能需要登入:',
                getTokenError
              );
              // 嘗試使用 login() 登入（需要 redirectUri）
              const redirectUri = window.location.href.split('#')[0]; // 移除 hash
              try {
                liff.login({ redirectUri });
                // login() 會重新導向，所以這裡不會繼續執行
                return;
              } catch (loginError) {
                console.warn('⚠️ [RegisterPage] 登入失敗:', loginError);
                throw getTokenError; // 拋出原始錯誤
              }
            }
          }
        } catch (tokenError) {
          console.warn(
            '⚠️ [RegisterPage] 取得 LINE ID Token 失敗:',
            tokenError
          );
          // 如果取得 token 失敗，在開發環境下使用 Mock
          if (shouldUseMockLiff()) {
            const mockUserId =
              localStorage.getItem('mockLineUserId') || 'U_MOCK_USER';
            lineIdToken = `mock_token_${mockUserId}_${Date.now()}`;
            console.log(
              '[RegisterPage] Mock LIFF 模式，生成假 LINE ID Token:',
              lineIdToken
            );
          } else {
            // 在生產環境，如果 LIFF 初始化失敗但 URL 中有 access_token，提示用戶
            if (accessTokenFromHash) {
              throw new Error(
                'LIFF 設定可能有誤，但 LINE 已提供 token。請檢查 LIFF ID 設定或聯繫管理員。'
              );
            } else {
              throw new Error(
                '無法取得 LINE ID Token，請確保已在 LINE 中開啟此頁面'
              );
            }
          }
        }
      } else if (shouldUseMockLiff()) {
        // Mock 模式下生成假的 LINE ID Token
        const mockUserId =
          localStorage.getItem('mockLineUserId') || 'U_MOCK_USER';
        lineIdToken = `mock_token_${mockUserId}_${Date.now()}`;
        console.log(
          '[RegisterPage] Mock LIFF 模式，生成假 LINE ID Token:',
          lineIdToken
        );
      } else {
        // 如果沒有 liff 實例，但 URL 中有 access_token，可能是 LIFF 初始化失敗
        if (accessTokenFromHash) {
          throw new Error(
            'LIFF 初始化失敗，但 LINE 已提供 token。請檢查 LIFF ID 設定。'
          );
        } else {
          throw new Error(
            '無法取得 LINE ID Token，請確保已在 LINE 中開啟此頁面'
          );
        }
      }

      if (!lineIdToken) {
        throw new Error('無法取得 LINE ID Token，請確保已在 LINE 中開啟此頁面');
      }

      console.log('🔍 [RegisterPage] 檢查 LINE ID 身份...');

      // 發送 GET /api/lite/auth/me 檢查身份
      await getUserByLineIdToken(lineIdToken);

      // 找到使用者，如果有 JWT Token 則導向，否則顯示註冊選項
      const jwtToken =
        localStorage.getItem('jwt_token') ||
        sessionStorage.getItem('jwt_token');
      if (jwtToken) {
        // 已登入，導向 Lite 主頁面
        const redirectTo = searchParams.get('redirect') || '/lite';
        navigate(redirectTo, { replace: true });
      } else {
        // 已找到用戶但沒有 JWT Token，直接註冊（因為 lite 版本不需要 Google OAuth）
        setStep('register');
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'USER_NOT_FOUND') {
        // LINE ID 不存在於系統中，這是正常情況（新用戶），顯示註冊選項
        console.log('ℹ️ [RegisterPage] 使用者不存在（新用戶），顯示註冊選項');
        setStep('register');
        setError(null); // 清除錯誤，因為這是正常情況
      } else {
        // 其他錯誤（網路錯誤、伺服器錯誤等），顯示錯誤訊息
        console.error('❌ [RegisterPage] 身份檢查失敗:', err);
        const errorMessage =
          err instanceof Error ? err.message : '檢查身份時發生錯誤';

        // 判斷是否為網路錯誤或伺服器錯誤
        if (
          errorMessage.includes('fetch') ||
          errorMessage.includes('network') ||
          errorMessage.includes('Network')
        ) {
          setError('網路連線失敗，請檢查您的網路連線後重試');
        } else if (
          errorMessage.includes('401') ||
          errorMessage.includes('403')
        ) {
          setError('認證失敗，請重新登入 LINE');
        } else if (
          errorMessage.includes('500') ||
          errorMessage.includes('伺服器')
        ) {
          setError('伺服器錯誤，請稍後再試');
        } else {
          setError(errorMessage);
        }
        setStep('register');
      }
    } finally {
      setIsLoading(false);
      hasCheckedRef.current = true;
    }
  }, [isReady, shouldUseLiff, liff, isLoggedIn, navigate, searchParams]);

  // 檢查 URL 參數中的回調結果
  useEffect(() => {
    const linkSuccess = searchParams.get('link_success');
    const linkError = searchParams.get('link_error');
    const jwtToken = searchParams.get('jwt_token') || searchParams.get('token');
    const errorMsg = searchParams.get('error');

    if (linkSuccess) {
      // 帳號關聯成功
      setStep('success');
      // 清除 URL 參數
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('link_success');
      newParams.delete('message');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${newParams.toString() ? `?${newParams.toString()}` : ''}`
      );

      // 如果有 JWT Token，保存並重新檢查
      if (jwtToken) {
        localStorage.setItem('jwt_token', jwtToken);
        hasCheckedRef.current = false;
        checkUserIdentity();
      }
      return;
    }

    if (linkError || errorMsg) {
      // 帳號關聯失敗或發生錯誤
      setError(errorMsg || '帳號關聯失敗，請稍後再試');
      setStep('register');
      // 清除 URL 參數
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('link_error');
      newParams.delete('error');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${newParams.toString() ? `?${newParams.toString()}` : ''}`
      );
      return;
    }

    if (jwtToken) {
      // 從 URL 取得 JWT Token（Google OAuth 回調）
      localStorage.setItem('jwt_token', jwtToken);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('jwt_token');
      newParams.delete('token');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${newParams.toString() ? `?${newParams.toString()}` : ''}`
      );

      // 登入成功，導向 Lite 主頁面
      const redirectTo = searchParams.get('redirect') || '/lite';
      navigate(redirectTo, { replace: true });
      return;
    }
  }, [searchParams, navigate, checkUserIdentity]);

  // 當 LIFF 準備好時，檢查用戶身份
  // 注意：checkUserIdentity 內部已經有完整的邏輯來處理 token 取得和錯誤處理
  useEffect(() => {
    if (isReady && !hasCheckedRef.current) {
      checkUserIdentity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // 從 OA 進入時沒有 groupId，此頁（建立群組用商店）需要 groupId；改導向 Square 入口由 OA 建立商家
  useEffect(() => {
    if (step !== 'register') return;
    const groupId = getGroupIdFromUrl();
    if (!groupId || !groupId.trim()) {
      navigate('/lite/new-square', { replace: true });
    }
  }, [step, navigate]);

  // 處理新用戶註冊（一次性註冊用戶並建立租戶）
  const handleRegister = async () => {
    if (!shouldUseLiff || (!liff && !shouldUseMockLiff())) {
      setError('請在 LINE 環境中使用此功能');
      return;
    }

    // 驗證表單
    if (!tenantForm.name.trim()) {
      setError('請輸入商店名稱');
      return;
    }

    if (!tenantForm.type.trim()) {
      setError('請選擇商店類型');
      return;
    }

    setIsLoading(true);
    setError(null);
    setStep('registering');

    // 先取得 groupId（在 try 外，以便在 catch 中使用）
    const groupId = getGroupIdFromUrl();

    try {
      // 取得 LINE ID Token
      // 注意：即使 isLoggedIn 為 false，也嘗試取得 token
      let lineIdToken: string | null = null;

      if (liff) {
        try {
          // 先檢查是否已登入
          if (isLoggedIn || liff.isLoggedIn()) {
            lineIdToken = await liff.getIDToken();
          } else {
            // 如果未登入，嘗試取得 token（可能會失敗）
            lineIdToken = await liff.getIDToken();
          }
        } catch (tokenError) {
          console.warn(
            '⚠️ [RegisterPage] 取得 LINE ID Token 失敗，嘗試使用 Mock 模式:',
            tokenError
          );
          // 如果取得 token 失敗，在開發環境下使用 Mock
          if (shouldUseMockLiff()) {
            const mockUserId =
              localStorage.getItem('mockLineUserId') || 'U_MOCK_USER';
            lineIdToken = `mock_token_${mockUserId}_${Date.now()}`;
            console.log(
              '[RegisterPage] Mock LIFF 模式，生成假 LINE ID Token:',
              lineIdToken
            );
          } else {
            throw new Error(
              '無法取得 LINE ID Token，請確保已在 LINE 中開啟此頁面'
            );
          }
        }
      } else if (shouldUseMockLiff()) {
        // Mock 模式下生成假的 LINE ID Token
        const mockUserId =
          localStorage.getItem('mockLineUserId') || 'U_MOCK_USER';
        lineIdToken = `mock_token_${mockUserId}_${Date.now()}`;
        console.log(
          '[RegisterPage] Mock LIFF 模式，生成假 LINE ID Token:',
          lineIdToken
        );
      }

      if (!lineIdToken) {
        throw new Error('無法取得 LINE ID Token，請確保已在 LINE 中開啟此頁面');
      }

      // 驗證 groupId
      if (!groupId || !groupId.trim()) {
        throw new Error(
          '無法取得 LINE 群組 ID，請確認 LIFF URL 包含 ?groupId=... 參數'
        );
      }

      console.log('[RegisterPage] ✅ 從 URL 取得 groupId:', groupId);

      console.log('✨ [RegisterPage] 一次性註冊新用戶並建立商店...', {
        groupId,
      });

      // 調用一次性註冊 API（包含建立租戶）
      const result = await registerNewUserWithTenant({
        lineIdToken,
        tenantName: tenantForm.name.trim(),
        tenantType: tenantForm.type.trim(),
        groupId: groupId,
      });

      // 註冊成功，保存 JWT Token
      localStorage.setItem('jwt_token', result.jwtToken);

      // 導向 Lite 主頁面，保留 groupId 在 query parameter 中（與 member 的 tenantId 一致）
      const redirectTo = searchParams.get('redirect') || '/lite';
      if (groupId) {
        // React Router 的 navigate() 會自動保留 query parameters
        const separator = redirectTo.includes('?') ? '&' : '?';
        navigate(
          `${redirectTo}${separator}groupId=${encodeURIComponent(groupId)}`,
          { replace: true }
        );
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      console.error('❌ [RegisterPage] 註冊失敗:', err);
      let errorMessage =
        err instanceof Error ? err.message : '註冊失敗，請稍後再試';

      // 處理特定錯誤訊息
      if (
        errorMessage.includes('此群組已註冊微型助理') ||
        errorMessage.includes('群組已註冊')
      ) {
        errorMessage = '此群組已註冊微型助理';
      } else if (
        errorMessage.includes('群組 ID') &&
        errorMessage.includes('必填')
      ) {
        errorMessage = '無法取得 LINE 群組 ID，請從群組中點入註冊';
      }

      // 記錄錯誤到後端
      await logErrorToBackend(err, {
        severity: 'error',
        fields: {
          event: 'register_user_with_tenant_failed',
          component: 'RegisterPage',
          userAction: 'register_with_tenant',
          errorCode:
            err instanceof Error && err.message.includes('此群組已註冊')
              ? 'GROUP_ALREADY_REGISTERED'
              : 'REGISTRATION_FAILED',
        },
        context: {
          groupId: groupId || undefined,
          tenantName: tenantForm.name,
          tenantType: tenantForm.type,
        },
      }).catch(logErr => {
        // 錯誤日誌記錄失敗時不影響主流程
        console.warn('[RegisterPage] 錯誤日誌記錄失敗:', logErr);
      });

      setError(errorMessage);
      setStep('register');
    } finally {
      setIsLoading(false);
    }
  };

  // 處理手動重新檢查
  const handleRefresh = () => {
    hasCheckedRef.current = false;
    checkUserIdentity();
  };

  // Loading 狀態
  if (isLoading && step === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            檢查身份中...
          </h2>
          <p className="text-sm text-gray-600">正在驗證您的 LINE 帳號</p>
        </div>
      </div>
    );
  }

  // 成功狀態
  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            帳號關聯成功！
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            您的 LINE 帳號已成功關聯到 POS 系統
          </p>
          <button
            onClick={handleRefresh}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            繼續使用
          </button>
        </div>
      </div>
    );
  }

  // 註冊狀態（未綁定）- 使用共用表單
  if (step === 'register' || step === 'registering') {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleRegister();
    };
    return (
      <LiteActivityAssistantForm
        value={tenantForm}
        onChange={setTenantForm}
        onSubmit={handleSubmit}
        submitting={step === 'registering'}
        error={error}
      />
    );
  }

  // 預設狀態（不應該到達這裡）
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <p className="text-gray-600">載入中...</p>
      </div>
    </div>
  );
}
